import { isNil } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import CommonLayout from '../../../common/layouts/commonLayout';
import Page from '../../../common/page/Page';
import AudioContainer from '../../../common/audio/audioContainer';
import PromptModal from '../../../common/components/PromptModal';
import ScreenErrorContainer from '../../../common/error/ScreenErrorContainer';
import RecordingVisualizer from '../../../common/recording/components/RecordingVisualizer';
import { TRC_CONFIRM_READING, ARIA_LABELS } from '../../../constants/text';
import { TRC_ARROW_BUTTON_DIRECTIONS } from '../../../constants/book';
import VRHelpPopup from '../../../common/components/helpPopup/VRHelpPopup';
import LoadingPage from '../../../common/components/LoadingPage';
import { SPINNER, Direction } from '../../../constants/components';
import ArrowButton from '../../../common/components/ArrowButton';
import RecordingContainer from '../../../common/recording/RecordingContainer';
import ShapedButton from '../../../common/components/ShapedButton';
import MicHelpPopup from '../../../common/components/helpPopup/MicHelpPopup';
import { TRO_READING, TRO_RECORDING }  from '../../../common/timeTracking/timeTrackingKeys';
import { MIC_FAILURE_TYPES } from '../instructions/TROInstructionsConstants';
import {setArPageTitle} from "../../troUtils";

const RECORDING_TIME_MS = 59000;

export default class TRCReadingView extends React.Component {
    constructor(props) {
        super(props);
        setArPageTitle(props.isExternalURLSourcePresent, 'Read Aloud');
        this.showNextPage = this.showNextPage.bind(this);
        this.onNextButtonClick = this.onNextButtonClick.bind(this);
        this.onBackButtonClick = this.onBackButtonClick.bind(this);
        this.handleBookReadingComplete = this.handleBookReadingComplete.bind(this);
        this.onRetry = this.onRetry.bind(this);
        this.onReturnToBook = this.onReturnToBook.bind(this);
        this.getBook = this.getBook.bind(this);
        this.onLogOut = this.onLogOut.bind(this);
        this.init = this.init.bind(this);
        this.onStartRecordingButtonClick = this.onStartRecordingButtonClick.bind(this);
        this.onStopRecordingButtonClick = this.onStopRecordingButtonClick.bind(this);
        this.state = {
            shouldStartRecording: false,
            shouldPauseRecording: false,
            shouldResumeRecording: false,
            shouldStopRecording: false,
            isVisualizerActive: false,
            isRecordingComplete: false,
            isOverlayActive: true,
            isMicHelpPopupVisible: false,
            micPopupProblem: MIC_FAILURE_TYPES.ACCESS_DENIED,
            areDoneReadingButtonsEnabled: !props.isExternalURLSourcePresent,
        };
    }

    componentDidMount() {
        this.props.startTimeTracking(TRO_READING);
        this.init();
    }


    componentDidUpdate(prevProps, prevState) {
        const {
            setBackButtonActive,
            isFirstTRCPage,
            executeApiCallSucceededEffect,
            executeApiCallFailedEffect,
            didApiCallSucceed,
            didApiCallFail,
            prevApiRequest,
            bookId,
            currentResult,
            micPopupProblem,
            isRecordingInProgress,
            trackStartTroReading,
            trackCompleteTroPageRecording,
            stopPageRecordingTimeTracking,
            isExternalURLSourcePresent,
        } = this.props;
        const { isRecordingComplete } = this.state;
        if (isRecordingComplete && isRecordingComplete !== prevState.isRecordingComplete) {
            setBackButtonActive(!isFirstTRCPage);
        }
        if (!isExternalURLSourcePresent && currentResult && !prevProps.currentResult) {
            trackStartTroReading();
        }
        if (!isExternalURLSourcePresent && !isRecordingInProgress && prevProps.isRecordingInProgress) {
            stopPageRecordingTimeTracking();
            trackCompleteTroPageRecording();
        }
        if (currentResult && didApiCallSucceed && !prevProps.didApiCallSucceed) {
            executeApiCallSucceededEffect(prevApiRequest, currentResult.id, bookId);
        }
        if (currentResult && didApiCallFail && !prevProps.didApiCallFail) {
            executeApiCallFailedEffect(prevApiRequest, currentResult.id, bookId);
        }
        if (micPopupProblem && !prevProps.micPopupProblem) {
            this.handleRecordingViewError();
        }
    }

    componentWillUnmount() {
        this.props.cleanup();
    }

    init() {
        const {
            isDataFetched,
            fetchContent,
            initializeResultId,
            showHelpPopup,
            hasUnscoredFailedResult,
        } = this.props;

        if (!isDataFetched) {
            fetchContent().then(this.props.initializeResultId);
        } else {
            initializeResultId();
        }
        if (hasUnscoredFailedResult) {
            showHelpPopup();
        }
    }

    showNextPage() {
        this.props.onNext(this.props.isLastTRCPage);
    }

    handleBookReadingComplete() {
        const {
            isRecordingSaved,
            recordedResult,
            completeReading,
            showSpinner,
            goToNextPage,
            trackReadTroBook,
            trackCompleteTroReading,
            isExternalURLSourcePresent,
        } = this.props;
        const { isRecordingComplete } = this.state;
        const isRecordingNotSaved = !isRecordingSaved && !isRecordingComplete && isNil(recordedResult);
        if (isRecordingNotSaved) {
            this.setState({
                shouldResumeRecording: false,
                isVisualizerActive: false,
                shouldPauseRecording: false,
                shouldStopRecording: true,
                isRecordingComplete: true,
            });
            completeReading();
            showSpinner();
        } else {
            goToNextPage();
        }

        if (isExternalURLSourcePresent) {
            trackReadTroBook();
        } else {
            trackCompleteTroReading();
        }
    }

    handleRecordingViewError() {
        const { micPopupProblem } = this.props;
        this.setState({isMicHelpPopupVisible: true, micPopupProblem});
    }

    onNextButtonClick() {
        const {
            isLastTRCPage,
            isExternalURLSourcePresent,
            trackTroReadingNavigationButton,
            activePage
        } = this.props;
        this.showNextPage();
        if (!isLastTRCPage && !this.state.isRecordingComplete) {
            this.setState({ isOverlayActive: true });
        }
        if (!isExternalURLSourcePresent) trackTroReadingNavigationButton(activePage, 'next-button');
    }

    onBackButtonClick() {
        const { activePage, onBack } = this.props;
        onBack(activePage);
    }

    onRetry() {
        this.props.initializeResultId();
    }

    onLogOut() {
        this.props.hideHelpPopup();
        this.props.showSpinner();
        this.props.logUserOut();
    }

    onReturnToBook() {
        const {
            isExternalURLSourcePresent,
            trackTroReadingNoButton,
            returnToBook,
        } = this.props;
        this.setState({ areDoneReadingButtonsEnabled: !isExternalURLSourcePresent });
        trackTroReadingNoButton();
        returnToBook();
    }

    // eslint-disable-next-line consistent-return
    onStartRecordingButtonClick() {
        const {
            isFirstTRCPage,
            recordedResult,
            isRecordingInProgress,
            recordingStream,
            isExternalURLSourcePresent,
            trackViewedTroBook,
            trackStartTroPageRecording,
            startPageRecordingTimeTracking,
            startTimeTracking,
        } = this.props;
        if (isNil(recordingStream)) return this.setState({ isMicHelpPopupVisible: true });
        const isReadyToStartRecording = isNil(recordedResult) && isFirstTRCPage && !isRecordingInProgress;

        if (isReadyToStartRecording) {
            this.setState({ shouldStartRecording: true, isVisualizerActive: true });
        } else {
            this.setState({ shouldResumeRecording: true, isVisualizerActive: true, shouldPauseRecording: false });
        }

        if (isExternalURLSourcePresent) {
            startTimeTracking(TRO_RECORDING);
            trackViewedTroBook();
        } else {
            startPageRecordingTimeTracking();
            trackStartTroPageRecording();
        }
    }

    onStopRecordingButtonClick() {
        const {
            isRecordingInProgress,
            trackStopTroPageRecording,
            trackCompleteTroBookRecording,
            isExternalURLSourcePresent,
            stopTimeTracking,
            trackRecordedTroBook,
        } = this.props;
        const baseState = { shouldStartRecording: false, shouldResumeRecording: false, isVisualizerActive: false, isOverlayActive: false };
        if (!isExternalURLSourcePresent) trackStopTroPageRecording();

        if (isRecordingInProgress) {
            this.setState({ ...baseState, shouldPauseRecording: true });
        } else {
            this.setState({ ...baseState, isRecordingComplete: true });
            if (!isExternalURLSourcePresent) trackCompleteTroBookRecording();
        }

        if (isExternalURLSourcePresent) {
            stopTimeTracking(TRO_RECORDING);
            trackRecordedTroBook();
        }
    }

    getBook() {
        const { pages, pageNum, isDataFetched, isRecordingInProgress, indented } = this.props;
        const { isRecordingComplete, isVisualizerActive, isOverlayActive } = this.state;
        const isRecordingInactive = !isRecordingInProgress && !isRecordingComplete && !isVisualizerActive;
        if (pages.length > 0) {
            const page = pages[pageNum];
            return (
                <div aria-hidden={this.props.isConfirmPopupActive} className="page-wrapper">
                    {isDataFetched && !isOverlayActive && !this.props.isFirstTRCPage && this.getBackNavigation()}
                    {isRecordingInactive && isOverlayActive
                        && <ShapedButton onClick={this.onStartRecordingButtonClick} text="Start recording" styles="start-recording-button" customTabIndex="0" />}
                    <Page
                        template={page.template}
                        paragraphs={page.paragraphs}
                        pictureAttributes={page.picture}
                        pageNumber={pageNum + 1}
                        indented={indented}
                        isTextHighlighted={isVisualizerActive}
                        onStopRecordingButtonClick={this.onStopRecordingButtonClick}
                    />
                    {isDataFetched && !isOverlayActive && this.getForwardNavigation()}
                </div>
            );
        }
        return false;
    }

    getForwardNavigation() {
        const { isLastTRCPage, isExternalURLSourcePresent, arLanguage } = this.props;
        const onInstructionAudioEnd = () => this.setState({ areDoneReadingButtonsEnabled: true });
        return (
            <ArrowButton
                ariaLabel={ARIA_LABELS.GO_TO_NEXT_BOOK_PAGE}
                isEnabled={!this.props.isConfirmPopupActive}
                onClick={isLastTRCPage ? () =>
                    this.props.onFinish(isExternalURLSourcePresent, onInstructionAudioEnd, arLanguage) : this.onNextButtonClick}
                pointingDirection={TRC_ARROW_BUTTON_DIRECTIONS.RIGHT}
            />
        );
    }

    getBackNavigation() {
        return (
            <ArrowButton
                ariaLabel={ARIA_LABELS.GO_TO_PREV_BOOK_PAGE}
                onClick={this.onBackButtonClick}
                pointingDirection={TRC_ARROW_BUTTON_DIRECTIONS.LEFT}
                isEnabled={this.props.isBackButtonActive && !this.props.isConfirmPopupActive}
            />
        );
    }

    render() {
        const {
            isScreenBlocked,
            isDataFetched,
            isHelpPopupVisible,
            isSpinnerVisible,
            isConfirmPopupActive,
            resultsVRStatuses,
            resultVRErrorReasons,
            saveRecordedResult,
            goToStart,
            isExternalURLSourcePresent,
            isBenchmarkModeEnabled,
            trackTroVrPopup,
            bookId,
            bookTitle,
            trackFailedEnableMic,
        } = this.props;
        const {
            shouldStartRecording,
            shouldPauseRecording,
            shouldResumeRecording,
            shouldStopRecording,
            isVisualizerActive,
            isOverlayActive,
            isMicHelpPopupVisible,
            micPopupProblem,
            areDoneReadingButtonsEnabled,
        } = this.state;
        const onRestart = () => goToStart(isExternalURLSourcePresent);
        return (
            <div>
                <CommonLayout class="trc-reading" isScreenBlocked={isScreenBlocked}>
                    {isDataFetched && (
                        <div>
                            <RecordingVisualizer
                                hasRecordingStatusBox
                                isVisualizerActive={isVisualizerActive}
                                isExternalURLSourcePresent={isExternalURLSourcePresent}
                            />
                            <RecordingContainer
                                duration={RECORDING_TIME_MS}
                                shouldStartRecording={shouldStartRecording}
                                shouldPauseRecording={shouldPauseRecording}
                                shouldResumeRecording={shouldResumeRecording}
                                shouldStopRecording={shouldStopRecording}
                                saveRecordedResult={saveRecordedResult}
                            />
                        </div>
                    )}
                    <AudioContainer />
                    <ScreenErrorContainer onRetry={this.onRetry} />
                    {isHelpPopupVisible && (
                        <VRHelpPopup
                            logUserOut={this.onLogOut}
                            resultsVRStatuses={resultsVRStatuses}
                            resultVRErrorReasons={resultVRErrorReasons}
                            isExternalURLSourcePresent={isExternalURLSourcePresent}
                            isBenchmarkModeEnabled={isBenchmarkModeEnabled}
                            bookId={bookId}
                            bookTitle={bookTitle}
                            trackTroVrPopup={trackTroVrPopup}
                        />
                    )}
                    {isMicHelpPopupVisible  &&
                        <MicHelpPopup onRestart={onRestart} problem={micPopupProblem} isExternalURLSourcePresent={isExternalURLSourcePresent} isBenchmarkModeEnabled={isBenchmarkModeEnabled} trackFailedEnableMic={trackFailedEnableMic} />}
                    {isOverlayActive && (
                        <div className="reading-overlay" />)}
                    {this.getBook()}
                    {isConfirmPopupActive && (
                        <PromptModal
                            message={TRC_CONFIRM_READING}
                            onConfirm={this.handleBookReadingComplete}
                            onCancel={this.onReturnToBook}
                            direction={Direction.LTR}
                            isEnabled={areDoneReadingButtonsEnabled}
                        />
                    )}
                </CommonLayout>
                {isSpinnerVisible && (
                    <LoadingPage spinnerConfig={SPINNER.DARK} />
                )}
            </div>
        );
    }
}

TRCReadingView.propTypes = {
    isDataFetched: PropTypes.bool,
    isScreenBlocked: PropTypes.bool,
    isBackButtonActive: PropTypes.bool,
    isRecordingInProgress: PropTypes.bool,
    indented: PropTypes.bool,
    initializeResultId: PropTypes.func,
    onNext: PropTypes.func,
    onBack: PropTypes.func,
    cleanup: PropTypes.func,
    fetchContent: PropTypes.func,
    completeReading: PropTypes.func.isRequired,
    goToNextPage: PropTypes.func,
    returnToBook: PropTypes.func,
    pages: PropTypes.array.isRequired,
    pageNum: PropTypes.number,
    isConfirmPopupActive: PropTypes.bool,
    recordedResult: PropTypes.object,
    isHelpPopupVisible: PropTypes.bool,
    logUserOut: PropTypes.func,
    resultsVRStatuses: PropTypes.array,
    showHelpPopup: PropTypes.func,
    hideHelpPopup: PropTypes.func,
    isFirstTRCPage: PropTypes.bool.isRequired,
    isLastTRCPage: PropTypes.bool.isRequired,
    setBackButtonActive: PropTypes.func,
    onFinish: PropTypes.func.isRequired,
    showSpinner: PropTypes.func,
    startTimeTracking: PropTypes.func,
    stopTimeTracking: PropTypes.func,
    isSpinnerVisible: PropTypes.bool,
    resultVRErrorReasons: PropTypes.array,
    isRecordingSaved: PropTypes.bool,
    saveRecordedResult: PropTypes.func,
    executeApiCallSucceededEffect: PropTypes.func,
    executeApiCallFailedEffect: PropTypes.func,
    didApiCallSucceed: PropTypes.bool,
    didApiCallFail: PropTypes.bool,
    prevApiRequest: PropTypes.object,
    bookId: PropTypes.string,
    bookTitle: PropTypes.string,
    currentResult: PropTypes.object,
    goToStart: PropTypes.func,
    recordingStream: PropTypes.shape({ clone: PropTypes.func.isRequired }),
    hasUnscoredFailedResult: PropTypes.bool.isRequired,
    micPopupProblem: PropTypes.string,
    isExternalURLSourcePresent: PropTypes.bool.isRequired,
    isBenchmarkModeEnabled: PropTypes.bool.isRequired,
    arLanguage: PropTypes.string.isRequired,
    trackViewedTroBook: PropTypes.func,
    trackRecordedTroBook: PropTypes.func,
    trackReadTroBook: PropTypes.func,
    activePage: PropTypes.number,
    trackStartTroReading: PropTypes.func,
    trackStartTroPageRecording: PropTypes.func,
    trackStopTroPageRecording: PropTypes.func,
    trackCompleteTroPageRecording: PropTypes.func,
    startPageRecordingTimeTracking: PropTypes.func,
    stopPageRecordingTimeTracking: PropTypes.func,
    trackCompleteTroBookRecording: PropTypes.func,
    trackCompleteTroReading: PropTypes.func,
    trackTroReadingNoButton: PropTypes.func,
    trackTroReadingNavigationButton: PropTypes.func,
    trackTroVrPopup: PropTypes.func,
    trackFailedEnableMic: PropTypes.func,
};

TRCReadingView.defaultProps = {
    isDataFetched: false,
    isScreenBlocked: false,
    isRecordingInProgress: false,
    isBackButtonActive: false,
    indented: false,
    recordedResult: null,
    isConfirmPopupActive: false,
    pageNum: undefined,
    onNext: () => {},
    onBack: () => {},
    fetchContent: () => {},
    initializeResultId: () => {},
    goToNextPage: () => {},
    returnToBook: () => {},
    cleanup: () => {},
    isHelpPopupVisible: false,
    logUserOut: () => {},
    resultsVRStatuses: [],
    showHelpPopup: () => {},
    hideHelpPopup: () => {},
    setBackButtonActive: () => {},
    showSpinner: () => {},
    startTimeTracking: () => {},
    stopTimeTracking: () => {},
    isSpinnerVisible: false,
    resultVRErrorReasons: [],
    isRecordingSaved: false,
    saveRecordedResult: () => {},
    executeApiCallSucceededEffect: () => {},
    executeApiCallFailedEffect: () => {},
    didApiCallSucceed: false,
    didApiCallFail: false,
    prevApiRequest: {},
    bookId: undefined,
    bookTitle: undefined,
    currentResult: null,
    goToStart: () => {},
    recordingStream: undefined,
    micPopupProblem: '',
    trackViewedTroBook: () => {},
    trackRecordedTroBook: () => {},
    trackReadTroBook: () => {},
    activePage: undefined,
    trackStartTroReading: () => {},
    trackStartTroPageRecording: () => {},
    trackStopTroPageRecording: () => {},
    trackCompleteTroPageRecording: () => {},
    startPageRecordingTimeTracking: () => {},
    stopPageRecordingTimeTracking: () => {},
    trackCompleteTroBookRecording: () => {},
    trackCompleteTroReading: () => {},
    trackTroReadingNoButton: () => {},
    trackTroReadingNavigationButton: () => {},
    trackTroVrPopup: () => {},
    trackFailedEnableMic: () =>{},
};

TRCReadingView.displayName = 'TRCReadingView';
